import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  Card,
  CardContent,
  Grid,
  Stack,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components
const ProgressContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2)
}));

const ProgressBar = styled(LinearProgress)(({ theme, value }) => ({
  height: 8,
  borderRadius: 4,
  flexGrow: 1,
  marginRight: theme.spacing(1),
  backgroundColor: theme.palette.grey[200],
  '& .MuiLinearProgress-bar': {
    borderRadius: 4,
    backgroundColor: value >= 90 ? theme.palette.error.main : 
                   value >= 75 ? theme.palette.warning.main : 
                   theme.palette.success.main
  }
}));

const AdTypeBubble = styled(Chip)(({ theme, adtype }) => {
  const getColorByAdType = (type) => {
    switch (type?.toLowerCase()) {
      case 'premium': return { bg: theme.palette.error.light, color: theme.palette.error.contrastText };
      case 'standard': return { bg: theme.palette.warning.light, color: theme.palette.warning.contrastText };
      case 'budget': return { bg: theme.palette.success.light, color: theme.palette.success.contrastText };
      default: return { bg: theme.palette.info.light, color: theme.palette.info.contrastText };
    }
  };

  const colors = getColorByAdType(adtype);
  
  return {
    backgroundColor: colors.bg,
    color: colors.color,
    fontWeight: 600,
    fontSize: '0.75rem',
    height: '28px',
    margin: theme.spacing(0.25),
    '&:hover': {
      backgroundColor: colors.bg,
      opacity: 0.8
    }
  };
});

const TopSpotIndicator = styled(Chip)(({ theme, available }) => ({
  backgroundColor: available ? theme.palette.success.main : theme.palette.error.main,
  color: available ? theme.palette.success.contrastText : theme.palette.error.contrastText,
  fontWeight: 600,
  fontSize: '0.8rem',
  marginBottom: theme.spacing(2),
  '&:hover': {
    backgroundColor: available ? theme.palette.success.main : theme.palette.error.main,
    opacity: 0.9
  }
}));

const ProductCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4]
  }
}));

/**
 * Component for displaying ad statistics for a single product type
 */
const ProductAdStatistics = ({ productType, stats, productDisplayName }) => {
  const { capacity, currentValue, percentage, advertisers, breakdown, topSpotTaken } = stats;
  
  // Only show top spot for ePublisher
  const showTopSpot = productType === 'ePublisher';
  
  const formatBreakdown = () => {
    if (productType === 'schoolLink') {
      return breakdown.total > 0 ? `${breakdown.total} Ads` : 'No ads';
    }
    
    const parts = [];
    if (breakdown.premium > 0) parts.push(`${breakdown.premium} Premium`);
    if (breakdown.standard > 0) parts.push(`${breakdown.standard} Standard`);
    if (breakdown.budget > 0) parts.push(`${breakdown.budget} Budget`);
    
    return parts.length > 0 ? parts.join(' • ') : 'No ads';
  };

  const renderAdTypeBubbles = () => {
    if (productType === 'schoolLink') {
      return breakdown.total > 0 ? (
        <AdTypeBubble 
          label={`${breakdown.total} Ads`} 
          adtype="default" 
          size="small" 
        />
      ) : null;
    }

    const bubbles = [];
    if (breakdown.premium > 0) {
      bubbles.push(
        <AdTypeBubble 
          key="premium" 
          label={`${breakdown.premium} Premium`} 
          adtype="premium" 
          size="small" 
        />
      );
    }
    if (breakdown.standard > 0) {
      bubbles.push(
        <AdTypeBubble 
          key="standard" 
          label={`${breakdown.standard} Standard`} 
          adtype="standard" 
          size="small" 
        />
      );
    }
    if (breakdown.budget > 0) {
      bubbles.push(
        <AdTypeBubble 
          key="budget" 
          label={`${breakdown.budget} Budget`} 
          adtype="budget" 
          size="small" 
        />
      );
    }

    return bubbles;
  };

  return (
    <ProductCard>
      <CardContent>
        {/* Top Spot Indicator - Only for ePublisher */}
        {showTopSpot && (
          <TopSpotIndicator
            available={!topSpotTaken}
            label={topSpotTaken ? "Top Spot Taken" : "Top Spot Available"}
            variant="filled"
          />
        )}

        {/* Product Type Header */}
        <Typography variant="h6" fontWeight="600" gutterBottom>
          {productDisplayName}
        </Typography>

        {/* Capacity Progress */}
        <ProgressContainer>
          <ProgressBar variant="determinate" value={percentage} />
          <Typography variant="body2" fontWeight="600" color="text.secondary" sx={{ minWidth: '80px' }}>
            {percentage}% Full
          </Typography>
        </ProgressContainer>

        {/* Capacity Numbers */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          ({currentValue}/{capacity})
        </Typography>

        {/* Advertiser Count */}
        <Typography variant="body1" fontWeight="600" sx={{ mb: 1 }}>
          {advertisers.length} Advertiser{advertisers.length !== 1 ? 's' : ''}
        </Typography>

        {/* Breakdown Text */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {formatBreakdown()}
        </Typography>

        {/* Ad Type Bubbles */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {renderAdTypeBubbles()}
        </Box>
      </CardContent>
    </ProductCard>
  );
};

/**
 * Main Ad Statistics Component
 */
const AdStatistics = ({ adStatistics, hasPaper, hasEPublisher, hasSchoolLink }) => {
  if (!adStatistics) {
    return null;
  }

  // Helper function to check if a value is truthy (handles boolean and numeric values)
  const isTrue = (value) => value === true || value === 1;

  // Filter product types based on what the school actually has
  const allProductTypes = [
    { key: 'paper', name: 'Paper', stats: adStatistics.paper, enabled: isTrue(hasPaper) },
    { key: 'ePublisher', name: 'ePublisher', stats: adStatistics.ePublisher, enabled: isTrue(hasEPublisher) },
    { key: 'schoolLink', name: 'SchoolLink', stats: adStatistics.schoolLink, enabled: isTrue(hasSchoolLink) }
  ];

  // Only show products that the school has enabled
  const productTypes = allProductTypes.filter(product => product.enabled);

  // If no products are enabled, don't show the statistics section
  if (productTypes.length === 0) {
    return null;
  }

  // Calculate grid size based on number of products (max 4 columns)
  const gridSize = Math.min(12 / productTypes.length, 4);

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" fontWeight="600" gutterBottom>
        Advertising Statistics
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Current ad capacity and breakdown for this school
      </Typography>

      <Grid container spacing={3}>
        {productTypes.map(({ key, name, stats }) => (
          <Grid item xs={12} md={gridSize} key={key}>
            <ProductAdStatistics
              productType={key}
              stats={stats}
              productDisplayName={name}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default AdStatistics;
